import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';
import { auth } from '../auth-compat';
import { CATALOGO, SITUACOES, TODOS_ITENS, itensDoColaborador, resumir, corAderencia, papeisDe } from './catalogo';
import { imprimirAuditoria } from './imprimir';

// Configurações → 📋 Auditoria de uso: checklist presencial por colaborador (usa / com dificuldade / não usa /
// não precisa / precisa de acesso), com o uso registrado no Quality ao lado para comparar com o que se vê ao vivo.
// Dados em api/auditoria-uso (tabela auditoria_uso); cada alteração guarda o "antes" no histórico da auditoria.
async function api(acao, dados = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  const r = await fetch(`/api/auditoria-uso?acao=${acao}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token || ''}` },
    body: JSON.stringify(dados),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `Erro ${r.status}`);
  return j;
}

const isoDe = d => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const hojeISO = () => isoDe(new Date());
const dataBR = iso => (iso ? new Date(String(iso).length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString('pt-BR') : '—');
const quando = iso => (iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const INICIO_TELAS = '2026-10-23'; // o Histórico de acessos registra telas abertas desde 23/10/2026
const RASCUNHO_LOCAL = 'quality-auditoria-uso-rascunho';
const formVazio = () => ({ id: null, colaboradorEmail: '', colaboradorNome: '', dataAuditoria: hojeISO(), situacao: 'rascunho', respostas: {}, conclusao: {}, historico: [] });

const estilo = {
  pagina: { padding: 20, background: '#f4f7f6', minHeight: 'calc(100vh - 140px)', boxSizing: 'border-box' },
  card: { background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', padding: 16, marginBottom: 14 },
  input: { padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13.5, boxSizing: 'border-box', maxWidth: '100%' },
  area: { width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13.5, fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical' },
  btn: { padding: '8px 14px', borderRadius: 8, border: 0, background: '#00897b', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 13.5 },
  btnClaro: { padding: '8px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#334155', fontWeight: 600, cursor: 'pointer', fontSize: 13.5 },
  btnPeq: { padding: '4px 10px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', color: '#334155', fontWeight: 600, cursor: 'pointer', fontSize: 12.5 },
  th: { textAlign: 'left', fontSize: 11.5, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc', whiteSpace: 'nowrap' },
  td: { padding: '8px 10px', borderBottom: '1px solid #f1f5f9', fontSize: 13, verticalAlign: 'top' },
  item: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 12px', marginBottom: 8 },
  op: { padding: '6px 11px', borderRadius: 18, border: '1.5px solid #cbd5e1', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', background: '#fff' },
  chip: { padding: '3px 9px', borderRadius: 14, border: '1px dashed #cbd5e1', background: '#fff', color: '#475569', fontSize: 12, cursor: 'pointer' },
  chipOn: { border: '1px solid #00897b', background: '#e0f2f1', color: '#00695c', fontWeight: 600 },
  rotulo: { fontSize: 11.5, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4 },
  kpi: { background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: '12px 14px', flex: '1 1 160px' },
};

function ItemLinha({ item, resp = {}, uso, usoDesde, onSituacao, onFuncao, onCampo }) {
  const pratica = item.tipo === 'pratica';
  if (item.emConstrucao) {
    return (
      <div style={{ ...estilo.item, background: '#f8fafc', color: '#94a3b8', fontSize: 13 }}>
        <s>{item.nome}</s> — {item.emConstrucao}; não conta na aderência
      </div>
    );
  }
  const opcoes = item.liberada ? ['usa', 'dificuldade', 'nao_usa', 'nao_precisa'] : ['precisa_acesso', 'nao_precisa'];
  const u = item.caminho && uso?.porTela?.[item.caminho];
  return (
    <div style={{ ...estilo.item, borderLeft: `4px solid ${resp.situacao ? SITUACOES[resp.situacao].cor : '#e2e8f0'}` }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ flex: '1 1 240px', minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 14 }}>{item.nome}</div>
          <div style={{ fontSize: 12, color: '#64748b', display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 2 }}>
            {!item.liberada && <span style={{ color: '#1e40af', fontWeight: 600 }}>sem acesso hoje</span>}
            {item.caminho && uso && item.liberada && (u
              ? <span title="Histórico de acessos do Quality">📈 abriu {u.vezes}× · último {dataBR(u.ultima)}</span>
              : <span style={{ color: '#b45309' }} title="Histórico de acessos do Quality">⚠ não abriu desde {dataBR(usoDesde)}</span>)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {opcoes.map(s => {
            const sel = resp.situacao === s;
            const cfg = SITUACOES[s];
            return (
              <button key={s} type="button" onClick={() => onSituacao(sel ? null : s)}
                style={{ ...estilo.op, background: sel ? cfg.cor : '#fff', color: sel ? '#fff' : cfg.cor, borderColor: sel ? cfg.cor : '#cbd5e1' }}>
                {pratica ? cfg.rotuloPratica : cfg.rotulo}
              </button>
            );
          })}
        </div>
      </div>
      {(item.funcoes || []).length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 11.5, color: '#64748b' }}>{item.liberada ? 'Viu usando:' : 'Deveria usar:'}</span>
          {item.funcoes.map(f => {
            const visto = (resp.funcoes || []).includes(f);
            return <button key={f} type="button" onClick={() => onFuncao(f)} style={{ ...estilo.chip, ...(visto ? estilo.chipOn : {}) }}>{visto ? '✔ ' : ''}{f}</button>;
          })}
        </div>
      )}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}>
        <label style={{ fontSize: 12.5, color: '#334155', display: 'flex', gap: 5, alignItems: 'center', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={!!resp.treinamento} onChange={e => onCampo('treinamento', e.target.checked)} /> Precisa de treinamento
        </label>
        <input style={{ ...estilo.input, flex: '1 1 240px' }} placeholder="Observação (o que viu, dúvida, sugestão…)" value={resp.obs || ''} onChange={e => onCampo('obs', e.target.value)} />
      </div>
    </div>
  );
}

export default function AuditoriaUso({ users = [] }) {
  const meuEmail = (auth.currentUser?.email || '').toLowerCase();
  const [aba, setAba] = useState('feitas'); // 'feitas' | 'form'
  const [auditorias, setAuditorias] = useState(null);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  const [form, setForm] = useState(formVazio);
  const [alterado, setAlterado] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [uso, setUso] = useState(null);
  const [busca, setBusca] = useState('');
  const [buscaColab, setBuscaColab] = useState('');
  const [verHistorico, setVerHistorico] = useState(false);
  const [verFaltam, setVerFaltam] = useState(false);
  const [rascunhoLocal, setRascunhoLocal] = useState(null);

  // colaboradores = logins do Quality (sem alunos "só INEP" e sem bloqueados)
  const colaboradores = useMemo(() => users
    .filter(u => u.email && !u.soInep && !u.bloqueado)
    .map(u => ({ ...u, email: u.email.toLowerCase() }))
    .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email, 'pt-BR')), [users]);
  const porEmail = useMemo(() => new Map(colaboradores.map(u => [u.email, u])), [colaboradores]);
  const colab = useMemo(() => porEmail.get(form.colaboradorEmail) || (form.colaboradorEmail ? { email: form.colaboradorEmail, name: form.colaboradorNome } : null), [porEmail, form.colaboradorEmail, form.colaboradorNome]);
  const meuNome = porEmail.get(meuEmail)?.name || meuEmail;
  const perfilDe = u => u?.perfilName || papeisDe(u).join(', ') || '—';
  const setorDe = u => [u?.setor, u?.equipe].filter(Boolean).join(' · ');

  const itens = useMemo(() => (colab ? itensDoColaborador(colab) : []), [colab]);
  const resumo = useMemo(() => resumir(itens, form.respostas), [itens, form.respostas]);
  const telasLiberadas = itens.filter(i => i.tipo !== 'pratica' && i.liberada && !i.emConstrucao).length;
  const usoDesde = (() => { const d30 = isoDe(new Date(Date.now() - 30 * 86400000)); return d30 > INICIO_TELAS ? d30 : INICIO_TELAS; })();

  const carregar = useCallback(() => api('listar').then(r => setAuditorias(r.auditorias || [])).catch(e => setErro(e.message)), []);
  useEffect(() => { carregar(); }, [carregar]);

  // uso registrado do colaborador (telas abertas e logins) para comparar com o que se vê ao vivo
  useEffect(() => {
    setUso(null);
    if (!form.colaboradorEmail) return undefined;
    let vivo = true;
    api('uso', { email: form.colaboradorEmail, dias: 30 }).then(r => { if (vivo) setUso(r); }).catch(() => {});
    return () => { vivo = false; };
  }, [form.colaboradorEmail]);

  // cópia local do formulário: se a página recarregar no meio da auditoria, dá para recuperar
  useEffect(() => {
    try { const r = JSON.parse(localStorage.getItem(RASCUNHO_LOCAL) || 'null'); if (r?.colaboradorEmail) setRascunhoLocal(r); } catch (_) { /* sem armazenamento */ }
  }, []);
  useEffect(() => {
    if (!alterado) return;
    try { localStorage.setItem(RASCUNHO_LOCAL, JSON.stringify({ ...form, guardadoEm: new Date().toISOString() })); } catch (_) { /* sem armazenamento */ }
  }, [form, alterado]);
  const esquecerRascunhoLocal = () => { try { localStorage.removeItem(RASCUNHO_LOCAL); } catch (_) { /* sem armazenamento */ } setRascunhoLocal(null); };

  const mudar = fn => { setForm(fn); setAlterado(true); };
  const responder = (id, campo, valor) => mudar(f => ({ ...f, respostas: { ...f.respostas, [id]: { ...(f.respostas[id] || {}), [campo]: valor } } }));
  const alternarFuncao = (id, fn) => mudar(f => {
    const atual = f.respostas[id]?.funcoes || [];
    const funcoes = atual.includes(fn) ? atual.filter(x => x !== fn) : [...atual, fn];
    return { ...f, respostas: { ...f.respostas, [id]: { ...(f.respostas[id] || {}), funcoes } } };
  });
  const mudarConclusao = (campo, valor) => mudar(f => ({ ...f, conclusao: { ...f.conclusao, [campo]: valor } }));

  const podeSair = () => !alterado || window.confirm('Há alterações não salvas nesta auditoria. Sair mesmo assim?');
  const novaAuditoria = (email = '') => {
    if (!podeSair()) return;
    setForm({ ...formVazio(), colaboradorEmail: email, colaboradorNome: porEmail.get(email)?.name || '' });
    setAlterado(false); setVerHistorico(false); setBuscaColab(''); setErro(''); setAba('form');
  };
  const abrir = a => {
    if (!podeSair()) return;
    setForm({
      id: a.id, colaboradorEmail: a.colaborador_email, colaboradorNome: a.colaborador_nome || '', dataAuditoria: a.data_auditoria, situacao: a.situacao,
      respostas: a.dados?.respostas || {}, conclusao: a.dados?.conclusao || {}, historico: a.historico || [], auditor: a.auditor_nome, atualizadoEm: a.atualizado_em,
    });
    setAlterado(false); setVerHistorico(false); setErro(''); setAba('form');
  };
  const recuperarRascunho = () => {
    if (!podeSair()) return;
    const { guardadoEm, ...r } = rascunhoLocal;
    setForm({ ...formVazio(), ...r });
    setAlterado(true); setRascunhoLocal(null); setAba('form');
  };
  const voltarParaLista = () => { if (!podeSair()) return; setAlterado(false); setAba('feitas'); };

  async function salvar(situacao) {
    if (!form.colaboradorEmail) { setErro('Escolha o colaborador.'); return; }
    const faltam = resumo.aResponder - resumo.respondidas;
    if (situacao === 'concluida' && faltam > 0 && !window.confirm(`Faltam ${faltam} item(ns) sem resposta. Concluir mesmo assim?`)) return;
    setSalvando(true); setErro('');
    try {
      const { auditoria } = await api('salvar', {
        id: form.id, colaboradorEmail: form.colaboradorEmail, colaboradorNome: colab?.name || form.colaboradorNome || '', setor: setorDe(colab),
        dataAuditoria: form.dataAuditoria, situacao, aderencia: resumo.aderencia,
        dados: {
          respostas: form.respostas, conclusao: form.conclusao, resumo, perfil: perfilDe(colab), telasLiberadas,
          uso: uso ? { dias: uso.dias, logins: uso.logins, ultimoLogin: uso.ultimoLogin, telasAbertas: uso.telasAbertas, porTela: uso.porTela } : null,
        },
      });
      setForm(f => ({ ...f, id: auditoria.id, situacao: auditoria.situacao, historico: auditoria.historico || [], auditor: auditoria.auditor_nome, atualizadoEm: auditoria.atualizado_em }));
      setAlterado(false); esquecerRascunhoLocal();
      setAviso(situacao === 'concluida' ? '✅ Auditoria concluída e salva.' : '💾 Rascunho salvo.');
      setTimeout(() => setAviso(''), 4000);
      carregar();
    } catch (e) { setErro(e.message); } finally { setSalvando(false); }
  }

  async function apagar(a) {
    if (!window.confirm(`Apagar a auditoria de ${a.colaborador_nome || a.colaborador_email} (${dataBR(a.data_auditoria)})? Ela sai da lista, mas fica guardada no banco.`)) return;
    try {
      await api('apagar', { id: a.id });
      if (form.id === a.id) { setForm(formVazio()); setAlterado(false); setAba('feitas'); }
      carregar();
    } catch (e) { setErro(e.message); }
  }

  const restaurar = v => {
    if (!window.confirm(`Carregar a versão salva em ${quando(v.salvoEm)} por ${v.por}? Ela só passa a valer quando você salvar.`)) return;
    mudar(f => ({ ...f, respostas: v.dados?.respostas || {}, conclusao: v.dados?.conclusao || {}, dataAuditoria: v.dataAuditoria || f.dataAuditoria }));
    setVerHistorico(false);
  };

  // texto das dificuldades a partir do que foi marcado
  const preencherDificuldades = () => {
    const nomes = s => itens.filter(i => form.respostas[i.id]?.situacao === s && i.tipo !== 'pratica').map(i => i.nome);
    const treinar = itens.filter(i => form.respostas[i.id]?.treinamento).map(i => i.nome);
    const partes = [
      nomes('nao_usa').length && `Não usa: ${nomes('nao_usa').join(', ')}.`,
      nomes('dificuldade').length && `Usa com dificuldade: ${nomes('dificuldade').join(', ')}.`,
      treinar.length && `Treinar: ${treinar.join(', ')}.`,
    ].filter(Boolean);
    if (!partes.length) { setAviso('Nada marcado como "não usa", "com dificuldade" ou "precisa de treinamento".'); setTimeout(() => setAviso(''), 4000); return; }
    mudarConclusao('dificuldades', [form.conclusao.dificuldades, partes.join('\n')].filter(Boolean).join('\n'));
  };

  const cabImpressao = (u, extra = {}) => ({ colaborador: u?.name || '', email: u?.email || '', setor: setorDe(u), perfil: perfilDe(u), ...extra });
  const imprimirForm = emBranco => imprimirAuditoria({
    itens, cab: cabImpressao(colab, { data: form.dataAuditoria, auditor: form.auditor || meuNome }),
    respostas: form.respostas, conclusao: form.conclusao, resumo, uso, emBranco,
  });
  const imprimirSalva = a => {
    const u = porEmail.get(a.colaborador_email) || { email: a.colaborador_email, name: a.colaborador_nome };
    const respostas = a.dados?.respostas || {};
    const its = itensDoColaborador(u);
    imprimirAuditoria({
      itens: its, cab: { ...cabImpressao(u), setor: a.setor || setorDe(u), perfil: a.dados?.perfil || perfilDe(u), data: a.data_auditoria, auditor: a.auditor_nome },
      respostas, conclusao: a.dados?.conclusao, resumo: a.dados?.resumo || resumir(its, respostas), uso: a.dados?.uso,
    });
  };

  // ── Visão geral das auditorias (última de cada pessoa) ──
  const ultimasPorPessoa = useMemo(() => {
    const m = new Map();
    for (const a of auditorias || []) if (!m.has(a.colaborador_email)) m.set(a.colaborador_email, a); // lista vem da mais recente para a mais antiga
    return [...m.values()];
  }, [auditorias]);
  const painel = useMemo(() => {
    const comNota = ultimasPorPessoa.filter(a => a.aderencia != null);
    const media = comNota.length ? Math.round(comNota.reduce((s, a) => s + Number(a.aderencia), 0) / comNota.length) : null;
    const treinar = ultimasPorPessoa.filter(a => (a.dados?.resumo?.treinamento || 0) + (a.dados?.resumo?.dificuldade || 0) > 0).length;
    const acessos = ultimasPorPessoa.reduce((s, a) => s + (a.dados?.resumo?.precisa_acesso || 0), 0);
    const nomes = new Map(TODOS_ITENS.map(i => [i.id, i.nome]));
    const porTela = new Map();
    for (const a of ultimasPorPessoa) {
      for (const [id, r] of Object.entries(a.dados?.respostas || {})) {
        if (!r?.situacao || id.startsWith('pratica_')) continue;
        if (!porTela.has(id)) porTela.set(id, { id, nome: nomes.get(id) || id, usa: 0, dificuldade: 0, nao_usa: 0, nao_precisa: 0, precisa_acesso: 0 });
        porTela.get(id)[r.situacao]++;
      }
    }
    const telas = [...porTela.values()]
      .map(t => ({ ...t, problema: t.nao_usa + t.dificuldade }))
      .filter(t => t.problema > 0 || t.precisa_acesso > 0)
      .sort((a, b) => b.problema - a.problema || b.precisa_acesso - a.precisa_acesso)
      .slice(0, 12);
    const auditados = new Set(ultimasPorPessoa.map(a => a.colaborador_email));
    const faltam = colaboradores.filter(u => !auditados.has(u.email));
    return { media, treinar, acessos, telas, faltam };
  }, [ultimasPorPessoa, colaboradores]);

  const listaFiltrada = useMemo(() => {
    const t = busca.trim().toLowerCase();
    return (auditorias || []).filter(a => !t || (a.colaborador_nome || '').toLowerCase().includes(t) || a.colaborador_email.includes(t) || (a.setor || '').toLowerCase().includes(t) || (a.auditor_nome || '').toLowerCase().includes(t));
  }, [auditorias, busca]);

  function exportarCsv() {
    const nomes = new Map(TODOS_ITENS.map(i => [i.id, i.nome]));
    const lista = (resp, s) => Object.entries(resp || {}).filter(([id, r]) => r?.situacao === s && !id.startsWith('pratica_')).map(([id]) => nomes.get(id) || id).join(', ');
    const cab = ['data', 'colaborador', 'email', 'setor', 'perfil', 'aderencia_%', 'usa', 'com_dificuldade', 'nao_usa', 'nao_precisa', 'precisa_acesso', 'precisa_treinar', 'boas_praticas', 'situacao', 'auditor', 'telas_nao_usa', 'telas_com_dificuldade', 'telas_precisa_acesso', 'plano_de_acao', 'prazo'];
    const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const linhas = (auditorias || []).map(a => {
      const r = a.dados?.resumo || {};
      const c = a.dados?.conclusao || {};
      return [dataBR(a.data_auditoria), a.colaborador_nome, a.colaborador_email, a.setor, a.dados?.perfil, a.aderencia ?? '', r.usa, r.dificuldade, r.nao_usa, r.nao_precisa, r.precisa_acesso, r.treinamento,
        r.praticasTotal ? `${r.praticasSim}/${r.praticasTotal}` : '', a.situacao === 'concluida' ? 'Concluída' : 'Rascunho', a.auditor_nome,
        lista(a.dados?.respostas, 'nao_usa'), lista(a.dados?.respostas, 'dificuldade'), lista(a.dados?.respostas, 'precisa_acesso'), c.plano, c.prazo ? dataBR(c.prazo) : ''].map(esc).join(';');
    });
    const el = document.createElement('a');
    el.href = URL.createObjectURL(new Blob(['﻿' + [cab.join(';'), ...linhas].join('\n')], { type: 'text/csv;charset=utf-8' }));
    el.download = `auditoria-uso-quality-${hojeISO()}.csv`;
    el.click();
  }

  const colabFiltrados = useMemo(() => {
    const t = buscaColab.trim().toLowerCase();
    return colaboradores.filter(u => !t || (u.name || '').toLowerCase().includes(t) || u.email.includes(t) || (u.setor || '').toLowerCase().includes(t));
  }, [colaboradores, buscaColab]);
  const anterioresDoColab = (auditorias || []).filter(a => a.colaborador_email === form.colaboradorEmail && a.id !== form.id);

  const cabecalho = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
      <div>
        <h2 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 800, color: '#1e293b' }}>📋 Auditoria de uso do Quality</h2>
        <div style={{ color: '#64748b', fontSize: 13 }}>Checklist presencial: cada colaborador usa as telas e funções de que precisa? O uso registrado no Quality aparece ao lado para comparar.</div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button style={aba === 'feitas' ? estilo.btn : estilo.btnClaro} onClick={voltarParaLista}>Auditorias feitas{auditorias ? ` (${auditorias.length})` : ''}</button>
        <button style={aba === 'form' ? estilo.btn : estilo.btnClaro} onClick={() => novaAuditoria()}>➕ Nova auditoria</button>
      </div>
    </div>
  );

  return (
    <div style={estilo.pagina}>
      {cabecalho}
      {erro && <div style={{ ...estilo.card, background: '#fee2e2', color: '#7f1d1d', borderColor: '#fecaca' }}>{erro}</div>}
      {aviso && <div style={{ ...estilo.card, background: '#ecfdf5', color: '#065f46', borderColor: '#a7f3d0', padding: 10 }}>{aviso}</div>}
      {rascunhoLocal && !alterado && (
        <div style={{ ...estilo.card, background: '#fffbeb', borderColor: '#fde68a', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: 12 }}>
          <span style={{ flex: '1 1 260px', fontSize: 13.5, color: '#78350f' }}>
            Há uma auditoria não salva de <b>{porEmail.get(rascunhoLocal.colaboradorEmail)?.name || rascunhoLocal.colaboradorEmail}</b> (guardada neste aparelho em {quando(rascunhoLocal.guardadoEm)}).
          </span>
          <button style={estilo.btnPeq} onClick={recuperarRascunho}>Recuperar</button>
          <button style={estilo.btnPeq} onClick={esquecerRascunhoLocal}>Descartar</button>
        </div>
      )}

      {/* ─────────────── AUDITORIAS FEITAS ─────────────── */}
      {aba === 'feitas' && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
            <div style={estilo.kpi}><div style={estilo.rotulo}>Colaboradores auditados</div><div style={{ fontSize: 26, fontWeight: 800 }}>{ultimasPorPessoa.length}<span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}> de {colaboradores.length}</span></div></div>
            <div style={estilo.kpi}><div style={estilo.rotulo}>Aderência média</div><div style={{ fontSize: 26, fontWeight: 800, color: corAderencia(painel.media) }}>{painel.media == null ? '—' : `${painel.media}%`}</div></div>
            <div style={estilo.kpi}><div style={estilo.rotulo}>Precisam de treinamento</div><div style={{ fontSize: 26, fontWeight: 800, color: painel.treinar ? '#b45309' : '#1e293b' }}>{painel.treinar}</div></div>
            <div style={estilo.kpi}><div style={estilo.rotulo}>Acessos a liberar</div><div style={{ fontSize: 26, fontWeight: 800, color: painel.acessos ? '#1e40af' : '#1e293b' }}>{painel.acessos}</div></div>
          </div>

          {painel.telas.length > 0 && (
            <div style={estilo.card}>
              <div style={{ fontWeight: 800, color: '#1e293b', marginBottom: 8 }}>Telas com menos aderência <span style={{ fontWeight: 500, color: '#64748b', fontSize: 13 }}>(última auditoria de cada pessoa) — por onde começar os treinamentos</span></div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>{['Tela', 'Usa', 'Com dificuldade', 'Não usa', 'Precisa de acesso'].map(h => <th key={h} style={estilo.th}>{h}</th>)}</tr></thead>
                  <tbody>{painel.telas.map(t => (
                    <tr key={t.id}>
                      <td style={{ ...estilo.td, fontWeight: 600 }}>{t.nome}</td>
                      <td style={estilo.td}>{t.usa}</td>
                      <td style={{ ...estilo.td, color: t.dificuldade ? SITUACOES.dificuldade.cor : undefined, fontWeight: t.dificuldade ? 700 : 400 }}>{t.dificuldade}</td>
                      <td style={{ ...estilo.td, color: t.nao_usa ? SITUACOES.nao_usa.cor : undefined, fontWeight: t.nao_usa ? 700 : 400 }}>{t.nao_usa}</td>
                      <td style={{ ...estilo.td, color: t.precisa_acesso ? SITUACOES.precisa_acesso.cor : undefined }}>{t.precisa_acesso}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}

          <div style={estilo.card}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
              <input style={{ ...estilo.input, flex: '1 1 240px' }} placeholder="Buscar (colaborador, setor, auditor)" value={busca} onChange={e => setBusca(e.target.value)} />
              <button style={estilo.btnClaro} onClick={exportarCsv} disabled={!auditorias?.length}>⬇️ Exportar CSV</button>
            </div>
            {auditorias === null ? <div style={{ color: '#64748b' }}>Carregando…</div>
              : !auditorias.length ? <div style={{ color: '#64748b', padding: '10px 0' }}>Nenhuma auditoria ainda. Clique em <b>➕ Nova auditoria</b> e escolha o colaborador.</div>
                : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead><tr>{['Data', 'Colaborador', 'Aderência', 'Usa · Dific. · Não usa', 'Treinar', 'Acesso', 'Situação', 'Auditor', ''].map(h => <th key={h} style={estilo.th}>{h}</th>)}</tr></thead>
                      <tbody>{listaFiltrada.map(a => {
                        const r = a.dados?.resumo || {};
                        return (
                          <tr key={a.id}>
                            <td style={{ ...estilo.td, whiteSpace: 'nowrap' }}>{dataBR(a.data_auditoria)}</td>
                            <td style={estilo.td}><b>{a.colaborador_nome || a.colaborador_email}</b><div style={{ fontSize: 11.5, color: '#64748b' }}>{a.setor || a.dados?.perfil || a.colaborador_email}</div></td>
                            <td style={{ ...estilo.td, fontWeight: 800, color: corAderencia(a.aderencia) }}>{a.aderencia == null ? '—' : `${Math.round(a.aderencia)}%`}</td>
                            <td style={{ ...estilo.td, whiteSpace: 'nowrap' }}>{r.usa ?? 0} · {r.dificuldade ?? 0} · {r.nao_usa ?? 0}</td>
                            <td style={estilo.td}>{r.treinamento || 0}</td>
                            <td style={estilo.td}>{r.precisa_acesso || 0}</td>
                            <td style={estilo.td}>{a.situacao === 'concluida'
                              ? <span style={{ background: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: 10, fontSize: 11.5, fontWeight: 700 }}>Concluída</span>
                              : <span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 10, fontSize: 11.5, fontWeight: 700 }}>Rascunho</span>}</td>
                            <td style={{ ...estilo.td, fontSize: 12 }}>{a.auditor_nome || a.auditor_email}</td>
                            <td style={{ ...estilo.td, whiteSpace: 'nowrap' }}>
                              <button style={estilo.btnPeq} onClick={() => abrir(a)}>Abrir</button>{' '}
                              <button style={estilo.btnPeq} onClick={() => imprimirSalva(a)} title="Imprimir / salvar em PDF">🖨️</button>{' '}
                              <button style={{ ...estilo.btnPeq, color: '#b91c1c' }} onClick={() => apagar(a)} title="Apagar">🗑️</button>
                            </td>
                          </tr>
                        );
                      })}</tbody>
                    </table>
                  </div>
                )}
          </div>

          {colaboradores.length > 0 && (
            <div style={estilo.card}>
              <button style={{ ...estilo.btnPeq, border: 0, padding: 0, fontSize: 14, fontWeight: 800, color: '#1e293b', background: 'none' }} onClick={() => setVerFaltam(v => !v)}>
                {verFaltam ? '▾' : '▸'} Ainda não auditados ({painel.faltam.length})
              </button>
              {verFaltam && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 6, marginTop: 10 }}>
                  {painel.faltam.map(u => (
                    <div key={u.email} style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #f1f5f9', borderRadius: 8, padding: '6px 8px' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name || u.email}</div>
                        <div style={{ fontSize: 11.5, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{setorDe(u) || perfilDe(u)}</div>
                      </div>
                      <button style={estilo.btnPeq} onClick={() => novaAuditoria(u.email)}>Auditar</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ─────────────── FORMULÁRIO ─────────────── */}
      {aba === 'form' && (
        <>
          <div style={estilo.card}>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '2 1 300px' }}>
                <div style={estilo.rotulo}>Colaborador</div>
                {form.id ? (
                  <div style={{ fontWeight: 700, fontSize: 15, padding: '6px 0' }}>{colab?.name || form.colaboradorEmail} <span style={{ color: '#64748b', fontWeight: 500, fontSize: 13 }}>{form.colaboradorEmail}</span></div>
                ) : (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <input style={{ ...estilo.input, flex: '1 1 140px' }} placeholder="Buscar nome, e-mail ou setor" value={buscaColab} onChange={e => setBuscaColab(e.target.value)} />
                    <select style={{ ...estilo.input, flex: '2 1 200px' }} value={form.colaboradorEmail}
                      onChange={e => mudar(f => ({ ...f, colaboradorEmail: e.target.value, colaboradorNome: porEmail.get(e.target.value)?.name || '' }))}>
                      <option value="">— escolha ({colabFiltrados.length}) —</option>
                      {colabFiltrados.map(u => <option key={u.email} value={u.email}>{u.name || u.email}{u.setor ? ` · ${u.setor}` : ''}</option>)}
                    </select>
                  </div>
                )}
              </div>
              <div>
                <div style={estilo.rotulo}>Data</div>
                <input type="date" style={estilo.input} value={form.dataAuditoria} onChange={e => mudar(f => ({ ...f, dataAuditoria: e.target.value }))} />
              </div>
              <div style={{ flex: '1 1 160px' }}>
                <div style={estilo.rotulo}>Auditor</div>
                <div style={{ fontSize: 13.5, padding: '6px 0' }}>{form.id && form.auditor && form.auditor !== meuNome ? <>{form.auditor} <span style={{ color: '#64748b' }}>(ao salvar: {meuNome})</span></> : meuNome}</div>
              </div>
            </div>

            {colab && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 10, marginTop: 14, paddingTop: 12, borderTop: '1px solid #f1f5f9', fontSize: 13 }}>
                <div><div style={estilo.rotulo}>Perfil</div>{perfilDe(colab)}</div>
                <div><div style={estilo.rotulo}>Setor / equipe</div>{setorDe(colab) || '—'}</div>
                <div><div style={estilo.rotulo}>Telas liberadas</div>{telasLiberadas}</div>
                <div><div style={estilo.rotulo}>Logins (30 dias)</div>{uso ? <>{uso.logins}{uso.ultimoLogin && <span style={{ color: '#64748b' }}> · último {quando(uso.ultimoLogin)}</span>}</> : '…'}</div>
                <div><div style={estilo.rotulo}>Telas abertas desde {dataBR(usoDesde)}</div>{uso ? uso.telasAbertas : '…'}</div>
              </div>
            )}
            {anterioresDoColab.length > 0 && (
              <div style={{ marginTop: 10, fontSize: 12.5, color: '#475569', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                Auditorias anteriores:
                {anterioresDoColab.map(a => (
                  <button key={a.id} style={estilo.btnPeq} onClick={() => abrir(a)}>{dataBR(a.data_auditoria)} · <b style={{ color: corAderencia(a.aderencia) }}>{a.aderencia == null ? '—' : `${Math.round(a.aderencia)}%`}</b></button>
                ))}
              </div>
            )}
          </div>

          {colab && (
            <>
              {/* resumo fixo no topo enquanto rola o checklist */}
              <div style={{ ...estilo.card, position: 'sticky', top: 0, zIndex: 5, display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', padding: '10px 14px', boxShadow: '0 4px 12px rgba(15,23,42,.06)' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontSize: 26, fontWeight: 800, color: corAderencia(resumo.aderencia) }}>{resumo.aderencia == null ? '—' : `${resumo.aderencia}%`}</span>
                  <span style={{ fontSize: 12, color: '#64748b' }}>aderência</span>
                </div>
                <div style={{ fontSize: 12.5, color: '#334155', display: 'flex', gap: 10, flexWrap: 'wrap', flex: '1 1 260px' }}>
                  <span><b style={{ color: SITUACOES.usa.cor }}>{resumo.usa}</b> usa</span>
                  <span><b style={{ color: SITUACOES.dificuldade.cor }}>{resumo.dificuldade}</b> com dificuldade</span>
                  <span><b style={{ color: SITUACOES.nao_usa.cor }}>{resumo.nao_usa}</b> não usa</span>
                  <span><b style={{ color: SITUACOES.precisa_acesso.cor }}>{resumo.precisa_acesso}</b> precisa de acesso</span>
                  <span><b>{resumo.treinamento}</b> treinar</span>
                  <span style={{ color: '#64748b' }}>respondidos {resumo.respondidas}/{resumo.aResponder}</span>
                  {alterado && <span style={{ color: '#b45309', fontWeight: 700 }}>● não salvo</span>}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button style={estilo.btnClaro} disabled={salvando} onClick={() => salvar('rascunho')}>💾 Salvar rascunho</button>
                  <button style={estilo.btn} disabled={salvando} onClick={() => salvar('concluida')}>{salvando ? 'Salvando…' : '✅ Concluir'}</button>
                  <button style={estilo.btnClaro} onClick={() => imprimirForm(false)} title="Imprimir / salvar em PDF">🖨️</button>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', margin: '0 2px 12px', fontSize: 13 }}>
                <span style={{ color: '#475569', flex: '1 1 320px' }}>
                  Todas as telas do Quality aparecem. As marcadas <b style={{ color: '#1e40af' }}>sem acesso hoje</b> não estão liberadas para ele — se deveria ter, marque <b>Precisa de acesso</b>.
                </span>
                <button style={estilo.btnPeq} onClick={() => imprimirForm(true)} title="Checklist em branco com todas as telas, para preencher no papel">🖨️ Imprimir em branco</button>
                {form.historico?.length > 0 && <button style={estilo.btnPeq} onClick={() => setVerHistorico(v => !v)}>🕘 Histórico ({form.historico.length})</button>}
                {form.atualizadoEm && <span style={{ color: '#64748b' }}>Salvo em {quando(form.atualizadoEm)}{form.situacao === 'concluida' ? ' · concluída' : ' · rascunho'}</span>}
              </div>

              {verHistorico && (
                <div style={estilo.card}>
                  <div style={{ fontWeight: 800, marginBottom: 8 }}>🕘 Versões anteriores desta auditoria <span style={{ fontWeight: 500, color: '#64748b', fontSize: 13 }}>(cópia do "antes" de cada alteração)</span></div>
                  {form.historico.map((v, i) => (
                    <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: '6px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}>
                      <span style={{ flex: '1 1 260px' }}>💾 {quando(v.salvoEm)} · {v.por} · {v.situacao === 'concluida' ? 'concluída' : 'rascunho'} · aderência {v.aderencia == null ? '—' : `${Math.round(v.aderencia)}%`}</span>
                      <button style={estilo.btnPeq} onClick={() => restaurar(v)}>Restaurar</button>
                    </div>
                  ))}
                </div>
              )}

              {CATALOGO.map(g => {
                const doGrupo = g.itens.map(i => itens.find(x => x.id === i.id)).filter(Boolean);
                if (!doGrupo.length) return null;
                const respondidos = doGrupo.filter(i => form.respostas[i.id]?.situacao).length;
                return (
                  <div key={g.grupo} style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', margin: '4px 2px 8px' }}>
                      <div style={{ fontWeight: 800, color: '#00695c', textTransform: 'uppercase', letterSpacing: '.05em', fontSize: 13 }}>{g.grupo}</div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>{respondidos}/{doGrupo.filter(i => !i.emConstrucao).length}</div>
                    </div>
                    {doGrupo.map(it => (
                      <ItemLinha key={it.id} item={it} resp={form.respostas[it.id]} uso={uso} usoDesde={usoDesde}
                        onSituacao={s => responder(it.id, 'situacao', s)}
                        onFuncao={f => alternarFuncao(it.id, f)}
                        onCampo={(c, v) => responder(it.id, c, v)} />
                    ))}
                  </div>
                );
              })}

              <div style={estilo.card}>
                <div style={{ fontWeight: 800, color: '#1e293b', marginBottom: 10 }}>Conclusão</div>
                <div style={{ display: 'grid', gap: 12 }}>
                  <div>
                    <div style={estilo.rotulo}>Pontos fortes</div>
                    <textarea rows={2} style={estilo.area} value={form.conclusao.fortes || ''} onChange={e => mudarConclusao('fortes', e.target.value)} />
                  </div>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <div style={estilo.rotulo}>Dificuldades e treinamentos necessários</div>
                      <button style={estilo.btnPeq} onClick={preencherDificuldades}>Preencher com o que foi marcado</button>
                    </div>
                    <textarea rows={3} style={estilo.area} value={form.conclusao.dificuldades || ''} onChange={e => mudarConclusao('dificuldades', e.target.value)} />
                  </div>
                  {resumo.precisa_acesso > 0 && (
                    <div>
                      <div style={estilo.rotulo}>Acessos a liberar (Configurações → Usuários)</div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {itens.filter(i => form.respostas[i.id]?.situacao === 'precisa_acesso').map(i => (
                          <span key={i.id} style={{ background: SITUACOES.precisa_acesso.fundo, color: SITUACOES.precisa_acesso.cor, padding: '3px 10px', borderRadius: 12, fontSize: 12.5, fontWeight: 600 }}>{i.nome}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: '3 1 300px' }}>
                      <div style={estilo.rotulo}>Plano de ação</div>
                      <textarea rows={2} style={estilo.area} value={form.conclusao.plano || ''} onChange={e => mudarConclusao('plano', e.target.value)} placeholder="Ex.: treinar Captação com a Enf. X na semana que vem; liberar Exames" />
                    </div>
                    <div style={{ flex: '1 1 150px' }}>
                      <div style={estilo.rotulo}>Prazo</div>
                      <input type="date" style={estilo.input} value={form.conclusao.prazo || ''} onChange={e => mudarConclusao('prazo', e.target.value)} />
                    </div>
                  </div>
                  <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13.5, cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!form.conclusao.ciente} onChange={e => mudarConclusao('ciente', e.target.checked)} />
                    O colaborador viu o resultado e está ciente do plano
                  </label>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
                  <button style={estilo.btnClaro} disabled={salvando} onClick={() => salvar('rascunho')}>💾 Salvar rascunho</button>
                  <button style={estilo.btn} disabled={salvando} onClick={() => salvar('concluida')}>{salvando ? 'Salvando…' : '✅ Concluir auditoria'}</button>
                  <button style={estilo.btnClaro} onClick={() => imprimirForm(false)}>🖨️ Imprimir / PDF</button>
                </div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 10 }}>
                  Aderência = telas que usa (inclusive com dificuldade) ÷ telas que precisa usar (inclusive as que precisa e ainda não tem acesso). "Não precisa", telas em construção e boas práticas ficam fora da conta.
                  O uso registrado vem do Histórico de acessos (telas desde 23/10/2026, logins desde 04/10/2026).
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
